import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { string, number, oneOf } from 'prop-types';
import classNames from 'classnames';
import { s_play } from '@fiverr-private/icons';
import { Icon } from '@fiverr-private/fit';
import { I18n } from '@fiverr-private/i18n-react';
import { ScaleFade } from '@fiverr-private/transition';
import { useObserver } from '@fiverr-private/orca';
import { getContext } from '@fiverr-private/fiverr_context';
import IntroVideoModal from '../IntroVideoModal/lazy';
import {
    INTRO_VIDEO_TIME,
    START_INTRO_VIDEO,
    PLAY_POSITIONS,
    ICON_SIZES
} from './constants';
import { introVideoImpression, introVideoHover, introVideoOpenModal, introVideoCloseModal, introVideoExists, introVideoError } from './events';
import classes from './index.module.scss';

export { PLAY_POSITIONS } from './constants';

const IntroVideo = ({
    videoUrl,
    origin,
    sellerName,
    sellerId,
    playPosition = PLAY_POSITIONS.CENTER,
    playButtonClassName,
    playButtonIconSize,
    overlayClassName
}) => {
    const { isTouch } = getContext();
    const [ isObserved, setObserved ] = useState(false);
    const [ showIntro, setShowIntro ] = useState(false);
    const [ showModal, setShowModal ] = useState(false);
    const readyForView = isObserved;
    const observableWrapperRef = useRef();
    const videoRef = useRef();
    const introTimeout = useRef();
    const eventParams = useMemo(() => ({ origin, sellerName, sellerId, videoUrl }), [origin, sellerName, sellerId, videoUrl]);
    const iconSize = playButtonIconSize || ICON_SIZES[playPosition];
    const isPlayCentered = playPosition === PLAY_POSITIONS.CENTER;

    useEffect(() => {
        if (!showIntro) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }
    }, [showIntro]);

    const onWrapperObserve = useCallback(() => {
        setObserved(true);
        introVideoImpression(eventParams);
    }, [eventParams]);

    useObserver({
        observableRef: observableWrapperRef,
        onObserve: onWrapperObserve,
        intersectionThreshold: 1
    });
    useEffect(() => {
        if (readyForView) {
            introTimeout.current = setTimeout(() => {
                setShowIntro(true);
                introTimeout.current = setTimeout(() => {
                    setShowIntro(false);
                }, INTRO_VIDEO_TIME);
            }, START_INTRO_VIDEO);

            return () => {
                clearTimeout(introTimeout.current);
            };
        } else {
            introVideoExists(eventParams);
        }
    }, [readyForView, eventParams]);

    const onMouseEnter = () => {
        if (!isTouch) {
            setShowIntro(true);
            clearTimeout(introTimeout.current);
            introVideoHover(eventParams);
        }
    };
    const onMouseLeave = () => {
        setShowIntro(false);
    };
    const openModal = () => {
        setShowModal(true);
        setShowIntro(false);
        introVideoOpenModal(eventParams);
    };
    const closeModal = (currentTime) => {
        setShowModal(false);
        introVideoCloseModal({ ...eventParams, currentTime });
    };

    const videoError = () => {
        introVideoError('profileImage', eventParams);
    };

    const blackBackgroundClasses = classNames(
        classes.blackBackground,
        { [classes.hoverable]: !isTouch }
    );

    const iconFrameClasses = classNames(
        classes.playIconFrame,
        {
            'box-shadow-z2': !isPlayCentered,
            [classes.hoverable]: !isTouch,
            [classes.centerIconFrame]: isPlayCentered,
            [classes.sideIconFrame]: !isPlayCentered
        },
        playButtonClassName
    );

    const iconClasses = classNames(
        classes.playIcon,
        {
            [classes.centerIcon]: isPlayCentered,
            [classes.sideIcon]: !isPlayCentered
        }
    );

    return (
        <>
            <div className={classes.wrapper}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={openModal}
                ref={observableWrapperRef}>
                <ScaleFade in={showIntro} initialScale={0.5} delay="slow1" duration="slow2">
                    <video ref={videoRef}
                        className={classes.video}
                        src={videoUrl} onError={videoError}
                        muted
                        loop
                        playsInline
                        preload="auto">
                        <I18n k="intro-video.browser-support"/>
                    </video>
                </ScaleFade>

                {isPlayCentered && (
                    <div className={blackBackgroundClasses}/>
                )}

                {!showIntro && <div className={iconFrameClasses}>
                    <Icon className={iconClasses} size={iconSize}>
                        {s_play}
                    </Icon>
                </div>}
            </div>
            <IntroVideoModal videoUrl={videoUrl} onClose={closeModal} isOpen={showModal} overlayClassName={overlayClassName}/>
        </>
    );
};

IntroVideo.propTypes = {
    videoUrl: string.isRequired,
    origin: string,
    sellerName: string,
    sellerId: number,
    playPosition: oneOf(Object.values(PLAY_POSITIONS)),
    playButtonClassName: string,
    playButtonIconSize: number,
    overlayClassName: string
};

export default IntroVideo;
